import type { WorkerRequest, WorkerRequestPayload, WorkerResponse } from '../types/LocalAiTypes';

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  timeout: ReturnType<typeof globalThis.setTimeout>;
  onProgress?: (progress: number) => void;
}

export class WorkerClient {
  private readonly pending = new Map<string, PendingRequest>();

  public constructor(private readonly worker: Worker) {
    worker.addEventListener('message', this.handleMessage);
    worker.addEventListener('error', this.handleError);
    worker.addEventListener('messageerror', this.handleMessageError);
  }

  public request<T>(
    request: WorkerRequestPayload,
    options: { timeoutMs?: number; transfer?: Transferable[]; onProgress?: (progress: number) => void } = {},
  ): Promise<T> {
    const requestId = crypto.randomUUID();
    return new Promise<T>((resolve, reject) => {
      const timeout = globalThis.setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error(`The local AI worker timed out while handling ${request.type}.`));
      }, options.timeoutMs ?? 120_000);
      this.pending.set(requestId, {
        resolve: (value) => resolve(value as T),
        reject,
        timeout,
        ...(options.onProgress === undefined ? {} : { onProgress: options.onProgress }),
      });
      this.worker.postMessage({ ...request, requestId } as WorkerRequest, options.transfer ?? []);
    });
  }

  public dispose(): void {
    this.worker.removeEventListener('message', this.handleMessage);
    this.worker.removeEventListener('error', this.handleError);
    this.worker.removeEventListener('messageerror', this.handleMessageError);
    this.worker.terminate();
    for (const pending of this.pending.values()) {
      globalThis.clearTimeout(pending.timeout);
      pending.reject(new Error('The local AI worker was disposed.'));
    }
    this.pending.clear();
  }

  private readonly handleMessage = (event: MessageEvent<WorkerResponse>): void => {
    const response = event.data;
    const pending = this.pending.get(response.requestId);
    if (!pending) return;
    if (response.type === 'PROGRESS') {
      pending.onProgress?.(response.progress);
      return;
    }
    globalThis.clearTimeout(pending.timeout);
    this.pending.delete(response.requestId);
    if (response.type === 'ERROR') pending.reject(new Error(response.error));
    else if (response.type === 'RESULT') pending.resolve(response.result);
    else pending.resolve(undefined);
  };

  /**
   * A failure the worker never got to report on itself.
   *
   * This fires for the errors that happen outside a request: the module failing to load, an
   * import throwing during evaluation, a nested worker the runtime spawned dying underneath it.
   * The browser gives an `ErrorEvent` whose `message` is very often empty for exactly those
   * cases — cross-origin script errors are censored to `""`, and a load failure carries nothing
   * at all — which is how this used to surface as the bare and unactionable "The local AI worker
   * failed". Where the message is missing the location usually is not, so it is reported
   * instead: a file and a line is the difference between a bug report and a shrug.
   */
  private readonly handleError = (event: ErrorEvent): void => {
    this.failAll(new Error(describeWorkerFailure(event)));
  };

  /**
   * A message that could not be deserialized — almost always a transfer that went wrong.
   *
   * Separate from `error` and easy to leave unhandled, in which case the request it belongs to
   * simply waits out its timeout and reports that instead of this.
   */
  private readonly handleMessageError = (): void => {
    this.failAll(new Error('The local AI worker sent a message that could not be read.'));
  };

  private failAll(error: Error): void {
    for (const pending of this.pending.values()) {
      globalThis.clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.pending.clear();
  }
}

/** Whatever the browser was willing to say about a worker that failed outside a request. */
function describeWorkerFailure(event: ErrorEvent): string {
  const message = event.message?.trim();
  const file = event.filename?.trim();
  const at = file ? `${file}${event.lineno ? `:${event.lineno}` : ''}` : '';
  if (message && at) return `The local AI worker failed: ${message} (${at})`;
  if (message) return `The local AI worker failed: ${message}`;
  if (at) return `The local AI worker could not start: ${at} failed to load or threw while loading.`;
  return 'The local AI worker could not start, and the browser reported no reason. '
    + 'The browser console holds the underlying error.';
}
